package kz.dentalcrm.app.screens.protocols.add

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.ime
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.BottomSheetDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.FilePlus
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.add
import dentalcrm.composeapp.generated.resources.code
import dentalcrm.composeapp.generated.resources.diagnosis_add_title
import dentalcrm.composeapp.generated.resources.protocol_add_title
import dentalcrm.composeapp.generated.resources.service_naming
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.screens.protocols.add.mvi.AddProtocolViewState
import kz.dentalcrm.app.screens.protocols.add.mvi.AddProtocolViewEvent
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddProtocolDialog(
    onAdd: () -> Unit,
    onDismiss: () -> Unit,
) {
    val modalBottomSheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val screenModel = koinViewModel<AddProtocolViewModel>()
    val state by screenModel.viewState.collectAsState()

    LaunchedEffect(state.screenState) {
        if (state.screenState is AddProtocolViewState.ScreenState.Added) {
            screenModel.handleEvents(AddProtocolViewEvent.Reset)
            onAdd()
            onDismiss()
        }
    }

    ModalBottomSheet(
        modifier = Modifier
            .widthIn(max = 450.dp)
            .imePadding()
            .statusBarsPadding(),
        onDismissRequest = {
            screenModel.handleEvents(AddProtocolViewEvent.Reset)
            onDismiss()
        },
        sheetState = modalBottomSheetState,
        dragHandle = { BottomSheetDefaults.DragHandle() },
        containerColor = DefaultTheme.colors.surfaceSecondary,
        content = {
            UiStateScaffold(
                isLoading = state.screenState is AddProtocolViewState.ScreenState.Loading,
                modifier = Modifier.background(Color.Transparent),
                content = {
                    AddServiceContent(
                        state = state,
                        event = screenModel::handleEvents
                    )
                }
            )
        }
    )
}

@Composable
private fun AddServiceContent(
    state: AddProtocolViewState,
    event: (AddProtocolViewEvent) -> Unit,
) {
    Column(
        modifier = Modifier
            .background(
                color = DefaultTheme.colors.surfaceSecondary,
                shape = RoundedCornerShape(topStart = 16.dp, topEnd = 16.dp)
            )
            .verticalScroll(rememberScrollState())
            .fillMaxWidth(),
    ) {
        var name by remember { mutableStateOf("") }
        var code by remember { mutableStateOf("") }

        Text(
            modifier = Modifier.padding(horizontal = 16.dp),
            text = stringResource(Res.string.protocol_add_title),
            style = DefaultTheme.typography.bodyLargeMedium,
            color = DefaultTheme.colors.onSurface
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        HorizontalDivider(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            thickness = 1.dp,
            color = DefaultTheme.colors.onSurfaceVariant.copy(
                alpha = 0.2f
            )
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            leadingIcon = FeatherIcons.FilePlus,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text
            ),
            singleLine = false,
            label = stringResource(Res.string.service_naming),
        ) {
            name = it
        }
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text
            ),
            singleLine = false,
            leadingIcon = FeatherIcons.FilePlus,
            label = stringResource(Res.string.code),
        ) {
            code = it
        }
        Spacer(modifier = Modifier.fillMaxWidth().height(24.dp))
        UIButton(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            enabled = name.isNotEmpty() && code.isNotEmpty(),
            text = stringResource(Res.string.add),
            isLoading = state.screenState is AddProtocolViewState.ScreenState.Adding,
            onClick = {
                event(
                    AddProtocolViewEvent.AddProtocol(
                        naming = name,
                        code = code,
                    )
                )
            },
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(32.dp).navigationBarsPadding())
    }
}